// components/project/economic/EconomicEntrateView.jsx
// V1.1 Step 3 — Vista Entrate operativa
//
// Fonti dati (CANONICHE — stessa source di Economico>Fatturazione e Riepilogo):
//   - GET /api/projects/{id}/milestones  (collection project_milestones, stato workflow reale)
//   - GET /api/projects/{id}/extras      (varianti extra approvate)
//   NB: NON usare più project.payment_milestones (array inline STALE = contratto originario).
//
// Stato = proiezione dello stato canonico milestone (status) + evidenze:
//   incassato   -> status 'incassato' o payment_date/paid_at
//   fatturato   -> status 'fatturato' o invoice_number/invoiced_at (non ancora incassato)
//   da_fatturare-> status 'da_fatturare'/'sbloccato' (SAL maturato, no fattura)
//   da_maturare -> status 'non_sbloccato'

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Search, Euro, FileText, Info } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtEur = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('it-IT'); } catch { return iso; }
};

const STATE_META = {
  incassato:    { label: 'Incassato',    color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  fatturato:    { label: 'Fatturato',    color: 'bg-blue-100 text-blue-800 border-blue-200' },
  da_fatturare: { label: 'Da fatturare', color: 'bg-amber-100 text-amber-800 border-amber-200' },
  da_maturare:  { label: 'Da maturare',  color: 'bg-slate-100 text-slate-700 border-slate-200' },
  da_verificare:{ label: 'Da verificare',color: 'bg-orange-100 text-orange-800 border-orange-200' },
};

const CATEGORY_META = {
  sal_cliente: 'SAL cliente',
  extra:       'Extra / Variante',
};

const EconomicEntrateView = ({ projectId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [projRes, milestonesRes, extrasRes] = await Promise.all([
          axios.get(`${API}/api/projects/${projectId}`),
          axios.get(`${API}/api/projects/${projectId}/milestones`).catch(() => ({ data: [] })),
          axios.get(`${API}/api/projects/${projectId}/extras`).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;

        const proj = projRes.data || {};
        // SOURCE CANONICA: collection project_milestones (stessa di Fatturazione/Riepilogo).
        const milestones = Array.isArray(milestonesRes.data)
          ? milestonesRes.data
          : (milestonesRes.data?.milestones || []);

        // ===== MILESTONE SAL =====
        // IVA SOURCE OF TRUTH: net/vat/gross calcolati dal BACKEND (endpoint /milestones,
        // cascade canonica). Nessuna aliquota hardcoded lato frontend.
        const salRows = milestones.map((m) => {
          const status = (m.status || '').toLowerCase();
          const isPaid = status === 'incassato' || !!(m.payment_date || m.paid_at);
          const isInvoiced = status === 'fatturato' || !!(m.invoice_number || m.invoiced_at);
          let state = 'da_verificare';
          if (isPaid) state = 'incassato';
          else if (isInvoiced) state = 'fatturato';
          else if (status === 'da_fatturare' || status === 'sbloccato') state = 'da_fatturare';
          else if (status === 'non_sbloccato') state = 'da_maturare';

          const netAmount = Number(m.amount_net ?? m.invoice_amount ?? m.amount ?? 0);
          const vat = Number(m.vat_amount ?? 0);
          const gross = Number(m.amount_gross ?? (netAmount + vat));
          return {
            id: `m-${m.id}`,
            voce: m.name || `SAL ${m.percentage || ''}%`,
            categoria: 'sal_cliente',
            categoriaLabel: 'SAL cliente',
            netto: netAmount,
            iva: vat,
            lordo: gross,
            vatRate: m.vat_rate ?? null,
            state,
            dataScadenza: m.due_date || null,
            dataIncasso: m.payment_date || m.paid_at || null,
            documentoLabel: m.invoice_number || null,
            fonte: 'Contratto lavori',
          };
        });

        // ===== EXTRA / VARIANTI =====
        // IVA SOURCE OF TRUTH: campi canonici sull'Extra (total = netto,
        // total_with_vat = lordo, vat_rate = aliquota reale della voce).
        const extras = Array.isArray(extrasRes.data) ? extrasRes.data : [];
        const extraRows = extras.map((e) => {
          const netAmount = Number(e.total ?? e.total_amount ?? e.amount ?? 0);
          const gross = Number(e.total_with_vat ?? netAmount);
          const iva = Math.round((gross - netAmount) * 100) / 100;
          const isDepositPaid = e.deposit_status === 'PAID' || e.deposit_status === 'paid';
          const isFinalPaid = e.final_status === 'PAID' || e.final_status === 'paid';
          const isApproved = e.approval_status === 'APPROVATO' || e.approval_status === 'approvato';
          let state = 'da_verificare';
          if (isFinalPaid) state = 'incassato';
          else if (isDepositPaid) state = 'fatturato';
          else if (isApproved) state = 'da_fatturare';
          else state = 'da_maturare';

          return {
            id: `e-${e.id}`,
            voce: `${e.extra_number || 'EXTRA'} — ${e.description || e.title || ''}`.trim(),
            categoria: 'extra',
            categoriaLabel: 'Extra / Variante',
            netto: netAmount,
            iva,
            lordo: gross,
            vatRate: e.vat_rate ?? null,
            state,
            dataScadenza: e.due_date || null,
            dataIncasso: e.paid_at || null,
            documentoLabel: e.extra_number || null,
            fonte: 'Extra approvato',
          };
        });

        setRows([...salRows, ...extraRows]);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Errore caricamento Entrate');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (projectId) load();
    return () => { cancelled = true; };
  }, [projectId]);

  const filteredRows = useMemo(() => {
    const query = q.trim().toLowerCase();
    return rows.filter(r => {
      if (categoryFilter !== 'all' && r.categoria !== categoryFilter) return false;
      if (statusFilter !== 'all' && r.state !== statusFilter) return false;
      if (query && !`${r.voce} ${r.categoriaLabel}`.toLowerCase().includes(query)) return false;
      return true;
    });
  }, [rows, q, statusFilter, categoryFilter]);

  const totals = useMemo(() => {
    const t = { venduto: 0, iva: 0, lordo: 0, fatturato: 0, incassato: 0, daIncassare: 0, daMaturare: 0 };
    for (const r of filteredRows) {
      t.venduto += r.netto;
      t.iva += r.iva;
      t.lordo += r.lordo;
      if (r.state === 'incassato') t.incassato += r.netto;
      if (r.state === 'fatturato' || r.state === 'incassato') t.fatturato += r.netto;
      if (r.state === 'fatturato') t.daIncassare += r.netto;
      if (r.state === 'da_maturare') t.daMaturare += r.netto;
    }
    return t;
  }, [filteredRows]);

  if (loading) {
    return <div className="p-8 text-center text-sm text-[#737373]" data-testid="qe-entrate-loading">Caricamento entrate...</div>;
  }
  if (error) {
    return (
      <Card className="card-abitalo"><CardContent className="p-6 text-center text-sm text-rose-600">{error}</CardContent></Card>
    );
  }

  if (rows.length === 0) {
    return (
      <Card className="card-abitalo" data-testid="qe-entrate-empty">
        <CardContent className="p-8 text-center">
          <Euro className="w-10 h-10 text-[#a3a3a3] mx-auto mb-3" />
          <h4 className="text-base font-semibold text-[#3C3C3C]">Nessuna entrata registrata</h4>
          <p className="text-sm text-[#737373] mt-1 max-w-md mx-auto">
            Le entrate compariranno qui appena il contratto lavori sara&apos; firmato, i SAL sbloccati o gli extra approvati.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="qe-entrate-view">
      {/* Filtri */}
      <div className="flex gap-2 flex-wrap" data-testid="qe-entrate-filters">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]" />
          <Input placeholder="Cerca voce..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-10" data-testid="qe-entrate-search" />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-44" data-testid="qe-entrate-category-filter"><SelectValue placeholder="Categoria" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le categorie</SelectItem>
            {Object.entries(CATEGORY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="qe-entrate-status-filter"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            {Object.entries(STATE_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Tabella */}
      <Card className="card-abitalo">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="qe-entrate-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[#525252] text-xs uppercase">
                  <th className="px-3 py-2 font-semibold">Voce</th>
                  <th className="px-3 py-2 font-semibold">Categoria</th>
                  <th className="px-3 py-2 font-semibold text-right">Netto</th>
                  <th className="px-3 py-2 font-semibold text-right">IVA</th>
                  <th className="px-3 py-2 font-semibold text-right">Totale lordo</th>
                  <th className="px-3 py-2 font-semibold">Stato</th>
                  <th className="px-3 py-2 font-semibold">Scadenza</th>
                  <th className="px-3 py-2 font-semibold">Incasso</th>
                  <th className="px-3 py-2 font-semibold">Documento</th>
                  <th className="px-3 py-2 font-semibold">Fonte</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => {
                  const st = STATE_META[r.state] || STATE_META.da_verificare;
                  return (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`qe-entrate-row-${r.id}`}>
                      <td className="px-3 py-2 font-medium">{r.voce}</td>
                      <td className="px-3 py-2 text-xs text-[#525252]">{r.categoriaLabel}</td>
                      <td className="px-3 py-2 text-right font-medium">{fmtEur(r.netto)}</td>
                      <td className="px-3 py-2 text-right text-xs text-[#737373]">{fmtEur(r.iva)}</td>
                      <td className="px-3 py-2 text-right font-semibold">{fmtEur(r.lordo)}</td>
                      <td className="px-3 py-2"><Badge className={`text-[10px] border ${st.color}`}>{st.label}</Badge></td>
                      <td className="px-3 py-2 text-xs text-[#525252]">{fmtDate(r.dataScadenza)}</td>
                      <td className="px-3 py-2 text-xs text-[#525252]">{fmtDate(r.dataIncasso)}</td>
                      <td className="px-3 py-2 text-xs">
                        {r.documentoLabel ? (
                          <span className="inline-flex items-center gap-1 text-[#525252]">
                            <FileText className="w-3 h-3" />{r.documentoLabel}
                          </span>
                        ) : <span className="text-[#a3a3a3]">—</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-[#737373]">{r.fonte}</td>
                    </tr>
                  );
                })}
              </tbody>
              {/* Totali footer */}
              <tfoot className="bg-emerald-50/50 border-t-2 border-emerald-200 font-semibold text-emerald-900">
                <tr data-testid="qe-entrate-totals">
                  <td className="px-3 py-2" colSpan={2}>Totali (filtrati: {filteredRows.length}/{rows.length})</td>
                  <td className="px-3 py-2 text-right">{fmtEur(totals.venduto)}</td>
                  <td className="px-3 py-2 text-right text-xs">{fmtEur(totals.iva)}</td>
                  <td className="px-3 py-2 text-right">{fmtEur(totals.lordo)}</td>
                  <td className="px-3 py-2 text-xs" colSpan={5}>
                    Fatt.: {fmtEur(totals.fatturato)} · Inc.: {fmtEur(totals.incassato)} · Da incassare: {fmtEur(totals.daIncassare)} · Da maturare: {fmtEur(totals.daMaturare)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="text-[11px] text-[#a3a3a3] italic px-1 pt-1 flex items-start gap-1">
        <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
        <span>
          Fonti CANONICHE: SAL cliente (/milestones, collection project_milestones) · Extra approvati (extras).
          Stessi stati/importi di Economico&gt;Fatturazione. Imponibile come valore principale; IVA e totale
          lordo calcolati dal backend per singola voce (aliquota reale, nessun default hardcoded).
        </span>
      </div>
    </div>
  );
};

export default EconomicEntrateView;
